/**
 * A browser call with an agent, for Try it. Ported from the call kit every agent's demo page
 * uses, and the two rules that kit is built around come with it - both were learned from a
 * real phone that went silent.
 *
 * ONE. AUDIO IS OPENED IN THE TAP. `openAudioContext()` and the `<audio>` element's `play()`
 * must run before the click handler's first `await`, and `resume()` is fired and never
 * awaited. A mobile browser lets a page start sound only while the tap that asked for it is
 * still live, and one fetch later it is not: Chrome on iOS then leaves the context suspended
 * with a `resume()` that never settles (an awaited one hangs the call on "calling..."), and an
 * Android in-app browser refuses the late `play()` so the call connects with a bot nobody can
 * hear. A DYNAMIC `import()` IS AN AWAIT TOO, which is why `prefetchClient()` exists: the
 * dialog loads the client when it opens, so the one in the tap is already resolved.
 *
 * TWO. THE BOT'S VOICE IS PLAYED THROUGH THE `<audio>` ELEMENT AND NOTHING ELSE. That is the
 * signal the browser's echo cancellation subtracts from what the microphone hears. Routed
 * through `audioCtx.destination` instead, Chrome's canceller flatlines: the agent hears
 * itself, transcribes itself and interrupts itself.
 */

import type { PipecatClient } from "@pipecat-ai/client-js";

type ClientModules = {
  PipecatClient: typeof import("@pipecat-ai/client-js").PipecatClient;
  DailyTransport: typeof import("@pipecat-ai/daily-transport").DailyTransport;
};

let loading: Promise<ClientModules> | null = null;

/**
 * The client, loaded once and on demand. It is a few hundred kilobytes that nobody who never
 * presses Try it should pay for, so it is not in the main bundle.
 */
export function prefetchClient(): Promise<ClientModules> {
  loading ??= Promise.all([import("@pipecat-ai/client-js"), import("@pipecat-ai/daily-transport")]).then(
    ([client, daily]) => ({ PipecatClient: client.PipecatClient, DailyTransport: daily.DailyTransport }),
  );
  return loading;
}

/**
 * An AudioContext, asked to run, for opening inside the tap (rule ONE). Null where a browser
 * cannot make one: the call still goes ahead, it only loses what the context was for.
 */
export function openAudioContext(): AudioContext | null {
  try {
    const Context = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Context) return null;
    const ctx = new Context();
    ctx.resume().catch(() => {});
    return ctx;
  } catch {
    return null;
  }
}

export interface CallHandlers {
  onConnected: () => void;
  onAgentSpeaking: (speaking: boolean) => void;
  onAgentText: (text: string) => void;
  onCallerText: (text: string, final: boolean) => void;
  /** The bot's audio track, for the `<audio>` element (rule TWO). */
  onBotTrack: (track: MediaStreamTrack) => void;
  /** The call is over: the agent hung up, the connection dropped, or the microphone failed. */
  onEnded: (reason: "hangup" | "microphone") => void;
}

/**
 * A client wired to the handlers. `connect(session)` takes Pipecat Cloud's start response as
 * it is: DailyTransport reads `dailyRoom` and `dailyToken` from it itself.
 */
export function buildClient(modules: ClientModules, on: CallHandlers): PipecatClient {
  return new modules.PipecatClient({
    transport: new modules.DailyTransport({
      // Stated explicitly and not left to defaults: Daily replaces constraints wholesale
      // rather than merging them, and all three matter for a voice call on a laptop.
      inputSettings: {
        audio: { settings: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } },
      },
    }),
    enableMic: true,
    enableCam: false,
    callbacks: {
      onBotReady: () => on.onConnected(),
      onBotStartedSpeaking: () => {
        on.onConnected();
        on.onAgentSpeaking(true);
      },
      onBotStoppedSpeaking: () => on.onAgentSpeaking(false),
      onBotTtsText: (data) => {
        if (data?.text) on.onAgentText(data.text);
      },
      onUserTranscript: (data) => {
        if (data?.text) on.onCallerText(data.text, Boolean(data.final));
      },
      onTrackStarted: (track, participant) => {
        if (track.kind === "audio" && !participant?.local) on.onBotTrack(track);
      },
      onDeviceError: () => on.onEnded("microphone"),
      // The agent hangs up by leaving the room; ending on that rather than on our own
      // connection dropping is what stops the dialog showing a live call to an empty room.
      onBotDisconnected: () => on.onEnded("hangup"),
      onDisconnected: () => on.onEnded("hangup"),
    },
  });
}
